import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { account, databases, DATABASE_ID, COLLECTIONS, convertDocumentToCamelCase, Query, ID } from '../lib/appwrite';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for active session
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const session = await account.get();
      console.log('Session found:', session);
      setUser(session);
      if (session?.$id) {
        await fetchProfile(session.$id);
      }
    } catch (error) {
      // No active session - this is expected for first load
      console.log('No active session (expected):', error.message);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async (userId) => {
    try {
      const data = await databases.getDocument(
        DATABASE_ID,
        COLLECTIONS.USER_PROFILES,
        userId
      );

      // Convert snake_case to camelCase
      const profileData = convertDocumentToCamelCase(data);
      setProfile(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If profile doesn't exist, it might be a new user
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      const session = await account.createEmailPasswordSession(email, password);
      setUser(session);
      await fetchProfile(session.userId);
      return { user: session };
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signUp = async (email, password, metadata = {}) => {
    try {
      // Create the user account
      const user = await account.create(
        ID.unique(),
        email,
        password,
        metadata.full_name || metadata.fullName || email.split('@')[0]
      );

      // Determine if this is the first user (will be admin)
      // Note: In production, you'd want to use an Appwrite Function to determine this
      const usersResponse = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.USER_PROFILES,
        [Query.limit(1)]
      );

      const isFirstUser = usersResponse.total === 0;

      // Create user profile in database
      const profileData = {
        email: email,
        full_name: metadata.full_name || metadata.fullName || email.split('@')[0],
        role: isFirstUser ? 'admin' : 'user',
        approved: isFirstUser ? true : false,
        email_verified: false
      };

      await databases.createDocument(
        DATABASE_ID,
        COLLECTIONS.USER_PROFILES,
        user.$id,
        profileData
      );

      // Auto sign-in after signup
      await signIn(email, password);

      return { user };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await account.deleteSession('current');
      setUser(null);
      setProfile(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    try {
      // Convert camelCase to snake_case for database
      const snakeCaseUpdates = {};
      Object.keys(updates).forEach(key => {
        // Convert camelCase to snake_case
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        snakeCaseUpdates[snakeKey] = updates[key];
      });

      const data = await databases.updateDocument(
        DATABASE_ID,
        COLLECTIONS.USER_PROFILES,
        user.$id || user.userId,
        snakeCaseUpdates
      );

      const profileData = convertDocumentToCamelCase(data);
      setProfile(profileData);
      return profileData;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await account.createRecovery(
        email,
        `${window.location.origin}/reset-password`
      );
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      await account.updatePassword(newPassword);
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  };

  const isAdmin = useMemo(() => {
    return profile?.role === 'admin' || profile?.role === 'super_admin';
  }, [profile?.role]);

  const isApproved = useMemo(() => {
    // Admin users are always considered approved
    if (profile?.role === 'admin' || profile?.role === 'super_admin') {
      return true;
    }
    return profile?.approved === true;
  }, [profile?.role, profile?.approved]);

  const value = useMemo(() => ({
    user,
    profile,
    loading,
    isAdmin,
    isApproved,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    fetchProfile,
  }), [user, profile, loading, isAdmin, isApproved]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
